// src/pages/admin/AdminClusters.tsx
//
// Where on-site learning should open next. Online launches first; families
// give their state + city when they enrol, and can ask to be told when
// hybrid or on-site opens near them. A cluster that reaches the threshold
// of active children is flagged as ready.

// Admin opens hybrid and/or on-site in an area (Phase 8): a venue, a session
// time and seats per mode. Families there can then choose those modes, and
// interested families are emailed when an area opens.
import { useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  useGetAreaOpeningsQuery,
  useGetClustersQuery,
  useOpenAreaMutation,
  useUpdateAreaMutation,
} from "../../app/api/libraryApi";
import type { AreaMode, AreaOpening } from "../../app/api/libraryApi";
import { errorText } from "../../app/api/learningApi";
import {
  EmptyState,
  PageHeader,
  Skeleton,
  StatCard,
  TableContainer,
  Td,
  Th,
} from "./AdminShared";

import { ArrowRight, Check, MapPin, MapPinOff, Star, Users } from "lucide-react";
const MODE_LABEL: Record<AreaMode, string> = { hybrid: "Hybrid", onsite: "On-site" };
const inputCls = "w-full rounded-xl border border-[#dde2ec] bg-white px-3 py-2 text-sm outline-none focus:border-[#1b2d5e]";

/** Open (or edit) hybrid / on-site in one area. */
function OpeningForm({
  area,
  existing,
  onDone,
}: {
  area: { state: string; city: string };
  existing: AreaOpening | null;
  onDone: () => void;
}) {
  const [openArea, { isLoading: opening }] = useOpenAreaMutation();
  const [updateArea, { isLoading: saving }] = useUpdateAreaMutation();
  const [modes, setModes] = useState<AreaMode[]>(existing?.modes ?? ["hybrid"]);
  const [venue, setVenue] = useState({
    name: existing?.venue.name ?? "",
    address: existing?.venue.address ?? "",
    sessionDay: existing?.venue.sessionDay ?? "",
    sessionTime: existing?.venue.sessionTime ?? "",
  });
  const [seats, setSeats] = useState<Record<AreaMode, string>>({
    hybrid: existing?.capacity.hybrid ? String(existing.capacity.hybrid) : "",
    onsite: existing?.capacity.onsite ? String(existing.capacity.onsite) : "",
  });
  const [notify, setNotify] = useState(true);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  const toggle = (m: AreaMode) => setModes((ms) => (ms.includes(m) ? ms.filter((x) => x !== m) : [...ms, m]));
  const submit = async () => {
    setNote(null);
    const body = {
      modes,
      venue,
      capacity: { hybrid: seats.hybrid ? Number(seats.hybrid) : null, onsite: seats.onsite ? Number(seats.onsite) : null },
      notify,
    };
    try {
      const res = existing
        ? await updateArea({ id: existing.id, status: "open", ...body }).unwrap()
        : await openArea({ ...area, ...body }).unwrap();
      setNote({ ok: true, text: res.message });
      setTimeout(onDone, 1200);
    } catch (e) {
      setNote({ ok: false, text: errorText(e, "Could not save") });
    }
  };

  return (
    <div className="mb-6 rounded-[22px] border border-[#1b2d5e] bg-[#f9fafc] p-5">
      <p className="text-sm font-bold text-[#16213e]">
        {existing ? "Edit" : "Open"} learning in {area.city}, {area.state}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {(["hybrid", "onsite"] as AreaMode[]).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => toggle(m)}
            className={`rounded-full px-4 py-1.5 text-xs font-semibold ${
              modes.includes(m) ? "bg-[#1b2d5e] text-white" : "border border-[#dde2ec] bg-white text-[#3b4560]"
            }`}
          >
            {modes.includes(m) && <><Check className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}</>}
            {MODE_LABEL[m]}
          </button>
        ))}
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <input className={inputCls} placeholder="Venue name (e.g. FSL Ikeja Hub)" value={venue.name} onChange={(e) => setVenue({ ...venue, name: e.target.value })} />
        <input className={inputCls} placeholder="Address" value={venue.address} onChange={(e) => setVenue({ ...venue, address: e.target.value })} />
        <input className={inputCls} placeholder="Session day (e.g. Saturdays)" value={venue.sessionDay} onChange={(e) => setVenue({ ...venue, sessionDay: e.target.value })} />
        <input className={inputCls} placeholder="Session time (e.g. 10:00–13:00)" value={venue.sessionTime} onChange={(e) => setVenue({ ...venue, sessionTime: e.target.value })} />
        {modes.map((m) => (
          <label key={m} className="text-xs font-semibold text-[#3b4560]">
            {MODE_LABEL[m]} seats (empty = no limit)
            <input className={`${inputCls} mt-1`} inputMode="numeric" value={seats[m]} onChange={(e) => setSeats({ ...seats, [m]: e.target.value.replace(/\D/g, "") })} />
          </label>
        ))}
      </div>
      <label className="mt-3 flex items-center gap-2 text-xs text-[#3b4560]">
        <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} className="accent-[#1b2d5e]" />
        Email families in this area who asked to hear when it opens
      </label>
      <p className="mt-2 text-[12.5px] text-[#8189a0]">
        Prices for each mode must be set in Settings first. A family's mode is fixed for a paid period: they switch when
        they enrol for the next one.
      </p>
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          disabled={opening || saving || !modes.length}
          onClick={submit}
          className="rounded-xl bg-[#1b2d5e] px-4 py-2 text-xs font-semibold text-white hover:bg-[#24397a] disabled:opacity-50"
        >
          {opening || saving ? "Saving…" : existing ? "Save" : "Open"}
        </button>
        <button type="button" onClick={onDone} className="rounded-xl px-3 py-2 text-xs font-semibold text-[#5f6780]">
          Cancel
        </button>
      </div>
      {note && <p className={`mt-2 text-xs font-semibold ${note.ok ? "text-[#2f7a4f]" : "text-[#b94a3a]"}`}>{note.text}</p>}
    </div>
  );
}

function OpenAreas({ onEdit }: { onEdit: (o: AreaOpening) => void }) {
  const { data } = useGetAreaOpeningsQuery();
  const [updateArea] = useUpdateAreaMutation();
  const list = data?.data ?? [];
  if (!list.length) return null;
  return (
    <div className="mb-6">
      <p className="mb-2 text-xs font-bold uppercase tracking-wider text-[#8189a0]">Open areas</p>
      <div className="grid gap-3 md:grid-cols-2">
        {list.map((o) => (
          <div key={o.id} className={`rounded-[18px] border p-4 ${o.status === "open" ? "border-[#d6efe2] bg-white" : "border-[#e5e8ef] bg-[#f9fafc] opacity-75"}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-sm font-bold capitalize text-[#16213e]">{o.city}, {o.state}</p>
                <p className="text-[12.5px] text-[#5f6780]">
                  {[o.venue.name, o.venue.sessionDay && `${o.venue.sessionDay}${o.venue.sessionTime ? `, ${o.venue.sessionTime}` : ""}`].filter(Boolean).join(" · ") || "No venue yet"}
                </p>
              </div>
              <span className={`rounded-full px-2 py-0.5 text-[11.5px] font-bold ${o.status === "open" ? "bg-[#edfaf4] text-[#2a7a56]" : "bg-[#f1f3f8] text-[#8189a0]"}`}>
                {o.status === "open" ? "Open" : "Closed"}
              </span>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {o.modes.map((m) => (
                <span key={m} className="rounded-full bg-[#ebeff7] px-2.5 py-0.5 text-[12.5px] font-semibold text-[#4a44b0]">
                  {MODE_LABEL[m]}: {o.seats[m]?.taken ?? 0}
                  {o.seats[m]?.capacity ? ` / ${o.seats[m]?.capacity} seats` : " enrolled"}
                </span>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <button type="button" onClick={() => onEdit(o)} className="rounded-xl border border-[#dde2ec] px-3 py-1 text-[12.5px] font-semibold text-[#3b4560]">Edit</button>
              <button
                type="button"
                onClick={() => {
                  if (o.status === "open" && !window.confirm(`Close ${o.city}? Enrolled families keep their place until their period ends; no new enrolments.`)) return;
                  updateArea({ id: o.id, status: o.status === "open" ? "closed" : "open", notify: false });
                }}
                className="rounded-xl px-3 py-1 text-[12.5px] font-semibold text-[#b94a3a]"
              >
                {o.status === "open" ? "Close" : "Reopen"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminClusters() {
  const { data, isLoading } = useGetClustersQuery();
  const { data: openings } = useGetAreaOpeningsQuery();
  const role = useSelector((s: { adminAuth: { user?: { role?: string } } }) => s.adminAuth.user?.role);
  const canOpen = role === "admin" || role === "super_admin";
  const [editing, setEditing] = useState<{ state: string; city: string; existing: AreaOpening | null } | null>(null);
  const d = data?.data;
  const openingFor = (id?: string) => openings?.data.find((o) => o.id === id) ?? null;

  return (
    <>
      <PageHeader
        title="Clusters"
        subtitle="Where our families live, and where on-site and hybrid learning should open first."
        action={
          <Link
            to="/admin/settings"
            className="rounded-2xl border border-[#dde2ec] px-4 py-2 text-xs font-semibold text-[#3b4560] hover:bg-[#f1f3f8]"
          >
            Prices & modes{" "}<ArrowRight className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
          </Link>
        }
      />

      {isLoading || !d ? (
        <div className="space-y-3">
          <Skeleton className="h-20 rounded-[18px]" />
          <Skeleton className="h-64 rounded-[18px]" />
        </div>
      ) : (
        <>
          <div className="mb-6 grid gap-3 sm:grid-cols-4">
            <StatCard label="Active children" value={String(d.totalActiveChildren)} icon={Users} color="#4a44b0" bg="#ebeff7" />
            <StatCard label="Clusters" value={String(d.clusters.length)} icon={MapPin} color="#2a7a56" bg="#edfaf4" />
            <StatCard
              label="Ready for on-site"
              value={String(d.clusters.filter((c) => c.readyForOnsite).length)}
              sub={`${d.threshold}+ active children`}
              icon={Star}
              color="#7a4a10"
              bg="#fff8ee"
            />
            <StatCard
              label="No location yet"
              value={String(d.unlocated.families)}
              sub={`${d.unlocated.activeChildren} active children`}
              icon={MapPinOff}
              color="#7a3020"
              bg="#fff4f1"
            />
          </div>

          <OpenAreas onEdit={(o) => setEditing({ state: o.state, city: o.city, existing: o })} />
          {editing && (
            <OpeningForm
              key={`${editing.state}|${editing.city}`}
              area={editing}
              existing={editing.existing}
              onDone={() => setEditing(null)}
            />
          )}

          {d.clusters.length === 0 ? (
            <EmptyState
              icon={MapPin}
              title="No locations yet"
              subtitle="Parents are asked where they live when they enrol. Clusters appear here as they do."
            />
          ) : (
            <TableContainer>
              <table className="w-full">
                <thead>
                  <tr>
                    <Th>Cluster</Th>
                    <Th>Families</Th>
                    <Th>Active children</Th>
                    <Th>Online</Th>
                    <Th>Want hybrid</Th>
                    <Th>Want on-site</Th>
                    <Th>Status</Th>
                    {canOpen && <Th> </Th>}
                  </tr>
                </thead>
                <tbody>
                  {d.clusters.map((c) => {
                    const pct = Math.min(100, Math.round((c.activeChildren / d.threshold) * 100));
                    return (
                      <tr key={c.key} className="border-t border-[#ebeff7]">
                        <Td>
                          <p className="text-xs font-bold text-[#16213e]">{c.city}</p>
                          <p className="text-[11.5px] text-[#8189a0]">
                            {c.state}
                            {c.areas.length > 0 && ` · ${c.areas.slice(0, 3).join(", ")}${c.areas.length > 3 ? "…" : ""}`}
                          </p>
                        </Td>
                        <Td>{c.families}</Td>
                        <Td>
                          <div className="w-28">
                            <p className="text-xs font-bold text-[#16213e]">
                              {c.activeChildren} / {d.threshold}
                            </p>
                            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-[#ebeff7]">
                              <div className="h-full bg-[#1b2d5e]" style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        </Td>
                        <Td>{c.onlineChildren}</Td>
                        <Td>{c.hybridInterest}</Td>
                        <Td>{c.onsiteInterest}</Td>
                        <Td>
                          {c.opening?.status === "open" ? (
                            <span className="rounded-full bg-[#ebeff7] px-2 py-0.5 text-[11.5px] font-bold text-[#4a44b0]">
                              Open: {c.opening.modes.map((m) => MODE_LABEL[m]).join(" + ")}
                            </span>
                          ) : c.readyForOnsite ? (
                            <span className="rounded-full bg-[#edfaf4] px-2 py-0.5 text-[11.5px] font-bold text-[#2a7a56]">
                              Ready to open
                            </span>
                          ) : (
                            <span className="rounded-full bg-[#f1f3f8] px-2 py-0.5 text-[11.5px] font-bold text-[#8189a0]">
                              Growing
                            </span>
                          )}
                        </Td>
                        {canOpen && (
                          <Td>
                            <button
                              type="button"
                              onClick={() => setEditing({ state: c.state, city: c.city, existing: openingFor(c.opening?.id) })}
                              className="rounded-xl border border-[#dde2ec] px-3 py-1 text-[12.5px] font-semibold text-[#3b4560] hover:bg-[#f1f3f8]"
                            >
                              {c.opening ? "Manage" : "Open here"}
                            </button>
                          </Td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </TableContainer>
          )}
        </>
      )}
    </>
  );
}
